import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ThreadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { InboxUtilsHooks } from '@frontend/inbox-api-utils';
import { InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { PERSON_PHONE_TYPES_ORDER, PERSON_PHONES_BY_TYPE } from './constants';
import { Conversation, GetPersonIO } from './types';

const NEW_THREAD_KEY = 'newInboxThreadData';

type GetNewThreadIdStringArgs = {
  personPhone: string;
  departmentId?: string;
};

export const getNewThreadIdString = (newThreadData: GetNewThreadIdStringArgs) => {
  return JSON.stringify({
    [NEW_THREAD_KEY]: newThreadData,
  });
};

export const isNewThreadId = (threadId: string) => {
  try {
    const parsed = JSON.parse(threadId);
    return NEW_THREAD_KEY in parsed;
  } catch {
    return false;
  }
};

/**
 * Sorts conversations by the priority of personPhones provided.
 * If multiple conversations have the same personPhone, their order will be most recent `lastModified` first.
 *
 * @param conversations - An unsorted array of conversations to be sorted
 * @param personPhoneOrder - An array of personPhones in order of priority
 */
export const orderConversationsByPriority = <T extends Pick<Conversation, 'personPhone' | 'lastModified'>>(
  conversations: T[],
  personPhoneOrder: string[]
) => {
  return [...conversations].sort((a, b) => {
    const personPhoneAIndex = personPhoneOrder.indexOf(a.personPhone);
    const personPhoneBIndex = personPhoneOrder.indexOf(b.personPhone);

    const indexA = personPhoneAIndex === -1 ? Infinity : personPhoneAIndex;
    const indexB = personPhoneBIndex === -1 ? Infinity : personPhoneBIndex;

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    if (a.lastModified > b.lastModified) return -1;
    if (a.lastModified < b.lastModified) return 1;
    return 0;
  });
};

type ResolveThreadGroupIdArgs = {
  providedThreadGroupId?: string;
  groupIds: string[];
  departmentId?: string;
  availableLocationPhones: InboxLocationPhonesTypes.LocationPhone[];
  getDataFromDepartmentId: ReturnType<
    typeof InboxUtilsHooks.useDepartmentLocationPhoneMappings
  >['getDataFromDepartmentId'];
};

/**
 * Resolves the thread group ID based on provided arguments in the global message action functions.
 * Priority order:
 * 1. Returns `providedThreadGroupId` if present.
 * 2. If only one `groupId` is available, returns it.
 * 3. If only one location phone is available, returns its locationId.
 * 4. If `departmentId` is provided, returns the locationId from department mapping.
 * 5. Returns `undefined` if none of the above apply.
 *
 * @param providedThreadGroupId (optional) - Explicit thread group ID to use if provided
 * @param groupIds - Array of possible group IDs
 * @param departmentId (optional) - Department ID for location mapping
 * @param availableLocationPhones - Array of available location phones
 * @param getDataFromDepartmentId - Function to get location mapping from department ID
 * @returns The resolved thread group ID, or undefined if not determinable
 */
export const resolveThreadGroupId = ({
  providedThreadGroupId,
  groupIds,
  departmentId,
  availableLocationPhones,
  getDataFromDepartmentId,
}: ResolveThreadGroupIdArgs) => {
  if (providedThreadGroupId) {
    return providedThreadGroupId;
  }

  if (groupIds.length === 1) {
    return groupIds[0];
  }

  if (availableLocationPhones.length === 1) {
    return availableLocationPhones[0].locationId;
  }

  if (departmentId) {
    return getDataFromDepartmentId(departmentId)?.locationId;
  }

  return undefined;
};

type ResolveLocationPhonesArgs = {
  allLocationPhones: InboxLocationPhonesTypes.LocationPhone[];
  providedLocationPhone?: string;
  departmentId?: string;
  threadGroupId?: string;
  groupIds: string[];
  getDataFromDepartmentId: ReturnType<
    typeof InboxUtilsHooks.useDepartmentLocationPhoneMappings
  >['getDataFromDepartmentId'];
};

/**
 * Resolves the availableLocationPhones based on provided arguments in the global message action functions.
 * Priority order:
 * 1. If `providedLocationPhone` is given, returns the matching location phone if it exists.
 * 2. If `departmentId` is provided, uses its mapped location phone to find and return the matching location phone.
 * 3. If `threadGroupId` is provided, returns all location phones for that group ID.
 * 4. Otherwise, returns all location phones for the provided `groupIds`.
 *
 * @param allLocationPhones - Array of all available location phones
 * @param providedLocationPhone (optional) - Explicit location phone to use if provided
 * @param departmentId (optional) - Department ID for location mapping
 * @param threadGroupId (optional) - Thread group ID to filter location phones
 * @param groupIds - Array of possible group IDs
 * @param getDataFromDepartmentId - Function to get location mapping from department ID
 * @returns An array of resolved location phones based on the provided criteria
 */
export const resolveLocationPhones = ({
  allLocationPhones,
  providedLocationPhone,
  departmentId,
  threadGroupId,
  groupIds,
  getDataFromDepartmentId,
}: ResolveLocationPhonesArgs) => {
  const resolvedProvidedLocationPhone = providedLocationPhone
    ? formatPhoneNumberE164(providedLocationPhone)
    : departmentId
    ? getDataFromDepartmentId(departmentId)?.locationPhone
    : undefined;
  if (resolvedProvidedLocationPhone) {
    const matchingLocationPhone = allLocationPhones.find(
      ({ locationPhone }) => locationPhone === resolvedProvidedLocationPhone
    );
    if (matchingLocationPhone) {
      return [matchingLocationPhone];
    }
  }

  if (threadGroupId) {
    return allLocationPhones.filter(({ locationId }) => locationId === threadGroupId);
  }

  return allLocationPhones.filter(({ locationId }) => groupIds.includes(locationId));
};

type ResolvePersonPhonesArgs = {
  providedPersonPhone?: string;
  providedPersonId?: string;
  groupIds: string[];
  fetchPerson: (request: GetPersonIO['input']) => Promise<Person>;
  getPhoneValidities: (personPhones: string[]) => Promise<{ personPhone: string; isValid: boolean }[]>;
};

/**
 * Resolves the availablePersonPhones based on provided arguments in the global message action functions, in order of priority.
 * Priority order:
 * 1. If `providedPersonPhone` is given, returns it in E.164 format.
 * 2. If `providedPersonId` and `groupIds` are given, fetches the person's contact info for those groups,
 *    formats the phone numbers to E.164, checks their validity, and returns the valid ones in priority order.
 * 3. Otherwise, returns an empty array.
 *
 * @param providedPersonPhone (optional) - Explicit person phone to use if provided
 * @param providedPersonId (optional) - Person ID to fetch contact info if no phone is provided
 * @param groupIds - Array of possible group IDs for fetching person contact info
 * @param fetchPerson - Function to fetch person data by ID and location IDs
 * @param getPhoneValidities - Function to check the validity of an array of phone numbers
 * @returns A promise that resolves to an array of resolved person phones in E.164 format
 */
export const resolvePersonPhones = async ({
  providedPersonPhone,
  providedPersonId,
  groupIds,
  fetchPerson,
  getPhoneValidities,
}: ResolvePersonPhonesArgs): Promise<string[]> => {
  if (providedPersonPhone) {
    return [formatPhoneNumberE164(providedPersonPhone)];
  }

  if (providedPersonId && !!groupIds.length) {
    const person = await fetchPerson({
      personId: providedPersonId,
      locationIds: groupIds,
    });

    const EMPTY_PERSON_PHONES_BY_TYPE: Record<PERSON_PHONES_BY_TYPE, string[]> = {
      [ContactType_Enum.PHONE_MOBILE]: [],
      [ContactType_Enum.PHONE_HOME]: [],
      [ContactType_Enum.PHONE_WORK]: [],
    } as const;

    const personPhonesByType =
      person.contactInfo?.reduce((acc, curr) => {
        if (!curr.destination) return acc;
        switch (curr.type) {
          case ContactType_Enum.PHONE_MOBILE:
          case ContactType_Enum.PHONE_WORK:
          case ContactType_Enum.PHONE_HOME:
            acc[curr.type] = Array.from(new Set([...acc[curr.type], formatPhoneNumberE164(curr.destination ?? '')]));
        }

        return acc;
      }, EMPTY_PERSON_PHONES_BY_TYPE) ?? EMPTY_PERSON_PHONES_BY_TYPE;

    const allPersonPhonesInOrder = PERSON_PHONE_TYPES_ORDER.flatMap((type) => personPhonesByType[type]);
    try {
      const allPersonPhoneValidities = await getPhoneValidities(allPersonPhonesInOrder);
      return allPersonPhonesInOrder.filter(
        (phone) => !!allPersonPhoneValidities.find(({ personPhone }) => personPhone === phone)?.isValid
      );
    } catch {
      // If validity check fails, assume all are valid
      return allPersonPhonesInOrder;
    }
  }

  return [];
};

export const resolveHighestPriorityConversation = ({
  conversations,
  defaultSmsLocationPhone,
  preferredOutboundLocationPhone,
}: {
  conversations: Conversation[];
  defaultSmsLocationPhone?: string;
  preferredOutboundLocationPhone?: string;
}) => {
  if (!conversations.length) return undefined;
  const mostRecentConversation = conversations[0];
  const allNewConversationThreads = conversations.every((conversation) => conversation.status === ThreadStatus.NEW);

  // if there is at least one active conversation, return the most recent conversation
  if (!allNewConversationThreads) return mostRecentConversation;

  // if all threads are new, follow this prioritized list of determining which thread to use:
  // (1) user's last used location phone (if available)
  if (preferredOutboundLocationPhone) {
    const lastUsedLocationPhoneConversation = conversations.find(
      (conversation) => conversation.locationPhone === preferredOutboundLocationPhone
    );
    if (lastUsedLocationPhoneConversation) return lastUsedLocationPhoneConversation;
  }

  // (2) default sms location phone
  if (defaultSmsLocationPhone) {
    const defaultSmsLocationPhoneConversation = conversations.find(
      (conversation) => conversation.locationPhone === defaultSmsLocationPhone
    );
    if (defaultSmsLocationPhoneConversation) return defaultSmsLocationPhoneConversation;
  }

  // (3) most recently modified conversation
  return mostRecentConversation;
};
